import { EmptyState } from "@/components/EmptyState";
import { LoadingPanel } from "@/components/LoadingPanel";
import { RepairComposer } from "@/components/RepairComposer";
import { RepairThread } from "@/components/RepairThread";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  REPAIR_PRIORITY_LABEL,
  REPAIR_STATUS_LABEL,
  useRepairTimeline,
  useRepairs,
  useRequestTriageLink,
} from "@/hooks/useRepairs";
import { useTenancy } from "@/hooks/useTenancy";
import { cn } from "@/lib/cn";
import { formatDateGB } from "@/lib/format";
import { type Repair, type RepairStatus, getSupabase } from "@/lib/supabase";
import { ChevronRight, MessageCircle, Star, Video, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STATUS_BADGE: Record<RepairStatus, string> = {
  reported: "bg-white/5 text-ink-dim border-line",
  triaged: "bg-blue-500/15 text-blue-200 border-blue-500/30",
  contractor_assigned: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  in_progress: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",
  completed: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-200 border-red-500/30",
};

export function Repairs() {
  const { tenancies, loading: tenanciesLoading } = useTenancy();
  const { repairs, loading: repairsLoading, error } = useRepairs();
  const [open, setOpen] = useState<Repair | null>(null);

  if (tenanciesLoading || repairsLoading) return <LoadingPanel />;

  const hasActiveTenancy = tenancies.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Repairs</span>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mt-2">Repairs</h1>
          <p className="text-sm text-ink-dim mt-1">
            Report problems with your property and track them through to fix.
          </p>
        </div>
        {hasActiveTenancy ? (
          <Button asChild>
            <Link to="/repairs/chat">
              <MessageCircle size={14} /> Report a repair
            </Link>
          </Button>
        ) : null}
      </header>

      {error ? (
        <Card>
          <CardContent className="py-6">
            <p role="alert" className="text-sm text-red-300">
              {error}
            </p>
          </CardContent>
        </Card>
      ) : null}

      {!hasActiveTenancy ? (
        <EmptyState
          title="No active tenancy"
          description="You'll be able to report repairs once your tenancy is active."
        />
      ) : repairs.length === 0 ? (
        <EmptyState
          title="No repairs reported"
          description="Tap Report a repair to start a chat with Trinium. We'll walk you through a fix or get an engineer on a video call."
        />
      ) : null}

      {repairs.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Your repairs</CardTitle>
            <CardDescription>Most recent first.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-line">
              {repairs.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setOpen(r)}
                    className="w-full text-left px-6 py-4 hover:bg-white/5 transition flex items-center gap-4"
                  >
                    <Wrench size={18} className="text-accent shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{r.title}</span>
                        <StatusPill status={r.status} />
                      </div>
                      <p className="text-xs text-ink-dim mt-1">
                        Reported {formatDateGB(r.created_at)}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-ink-dim shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {open ? <RepairDetail repair={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function FeedbackBlock({ repairId }: { repairId: string }) {
  const [existing, setExisting] = useState<{ rating: number; comment: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { data } = await sb
        .from("repair_feedback")
        .select("rating, comment")
        .eq("repair_id", repairId)
        .maybeSingle();
      if (!cancelled) {
        setExisting((data as { rating: number; comment: string | null } | null) ?? null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repairId]);

  async function submit() {
    if (rating < 1 || rating > 5) {
      setError("Pick a rating");
      return;
    }
    setSubmitting(true);
    setError(null);
    const sb = getSupabase();
    const { data: u } = await sb.auth.getUser();
    if (!u.user) {
      setError("Sign in expired");
      setSubmitting(false);
      return;
    }
    const { error: insErr } = await sb.from("repair_feedback").insert({
      repair_id: repairId,
      tenant_user_id: u.user.id,
      rating,
      comment: comment.trim() || null,
    });
    if (insErr) setError(insErr.message);
    else setExisting({ rating, comment: comment.trim() || null });
    setSubmitting(false);
  }

  if (loading) return null;

  if (existing) {
    return (
      <section>
        <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Your feedback</h3>
        <div className="flex items-center gap-1 mb-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={16}
              className={n <= existing.rating ? "fill-amber-400 text-amber-400" : "text-ink-dim"}
            />
          ))}
        </div>
        {existing.comment ? (
          <p className="text-sm whitespace-pre-wrap">{existing.comment}</p>
        ) : null}
        <p className="text-xs text-ink-dim mt-1">Thanks for the feedback.</p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">How did we do?</h3>
      <p className="text-sm text-ink-dim mb-3">Quick rating helps us pick contractors next time.</p>
      <div className="flex items-center gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            className="p-1"
          >
            <Star
              size={22}
              className={
                n <= rating ? "fill-amber-400 text-amber-400" : "text-ink-dim hover:text-ink"
              }
            />
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        placeholder="Anything else? (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="w-full rounded-xl border border-line bg-bg-card p-3 text-sm text-ink"
      />
      {error ? (
        <p role="alert" className="text-xs text-red-300 mt-1">
          {error}
        </p>
      ) : null}
      <div className="mt-3">
        <Button onClick={submit} disabled={submitting || rating === 0}>
          {submitting ? "Sending…" : "Send feedback"}
        </Button>
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: RepairStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider border",
        STATUS_BADGE[status],
      )}
    >
      {REPAIR_STATUS_LABEL[status]}
    </span>
  );
}

function RepairDetail({ repair, onClose }: { repair: Repair; onClose: () => void }) {
  const { events, loading } = useRepairTimeline(repair.id);
  const { request, submitting: triageSubmitting, error: triageError } = useRequestTriageLink();
  const canTriage = repair.status !== "completed" && repair.status !== "cancelled";

  async function startTriage() {
    const { link } = await request(repair.id);
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  }
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <aside
        className="bg-bg w-full max-w-xl h-full overflow-y-auto border-l border-line"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-line flex items-start justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.18em] text-ink-dim">Repair</span>
            <h2 className="text-xl font-semibold mt-1">{repair.title}</h2>
            <div className="mt-2">
              <StatusPill status={repair.status} />
            </div>
          </div>
          <button type="button" className="text-ink-dim hover:text-ink" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-6">
          {canTriage ? (
            <section className="flex flex-col gap-2">
              <Button onClick={startTriage} disabled={triageSubmitting}>
                <Video size={14} /> {triageSubmitting ? "Connecting…" : "Start video triage"}
              </Button>
              <p className="text-xs text-ink-dim">
                Connect to an engineer over video so they can see the problem and decide what's
                needed.
              </p>
              {triageError ? (
                <p role="alert" className="text-xs text-red-300">
                  {triageError}
                </p>
              ) : null}
            </section>
          ) : null}

          <section>
            <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Your report</h3>
            <p className="text-sm whitespace-pre-wrap">{repair.description}</p>
            <p className="text-xs text-ink-dim mt-2">
              Priority you flagged: {REPAIR_PRIORITY_LABEL[repair.reporter_priority]} · Reported{" "}
              {formatDateGB(repair.created_at)}
            </p>
          </section>

          {repair.triage_summary || repair.triaged_at ? (
            <section>
              <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Triage</h3>
              {repair.triage_summary ? (
                <p className="text-sm whitespace-pre-wrap">{repair.triage_summary}</p>
              ) : (
                <p className="text-sm text-ink-dim">Triage in progress…</p>
              )}
              {repair.triage_priority ? (
                <p className="text-xs text-ink-dim mt-2">
                  Triaged priority: {REPAIR_PRIORITY_LABEL[repair.triage_priority]} ·{" "}
                  {formatDateGB(repair.triaged_at)}
                </p>
              ) : null}
            </section>
          ) : null}

          {repair.contractor_name ? (
            <section>
              <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Contractor</h3>
              <p className="text-sm font-medium">{repair.contractor_name}</p>
              {repair.contractor_phone ? (
                <p className="text-sm">
                  <a href={`tel:${repair.contractor_phone}`} className="text-accent">
                    {repair.contractor_phone}
                  </a>
                </p>
              ) : null}
              {repair.contractor_email ? (
                <p className="text-sm">
                  <a href={`mailto:${repair.contractor_email}`} className="text-accent">
                    {repair.contractor_email}
                  </a>
                </p>
              ) : null}
              {repair.scheduled_at ? (
                <p className="text-xs text-ink-dim mt-2">
                  Scheduled for {formatDateGB(repair.scheduled_at)}
                </p>
              ) : null}
            </section>
          ) : null}

          {repair.completed_at ? (
            <section>
              <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Completed</h3>
              {repair.completion_notes ? (
                <p className="text-sm whitespace-pre-wrap">{repair.completion_notes}</p>
              ) : null}
              <p className="text-xs text-ink-dim mt-2">{formatDateGB(repair.completed_at)}</p>
            </section>
          ) : null}

          {repair.status === "completed" ? <FeedbackBlock repairId={repair.id} /> : null}

          <RepairThread repairId={repair.id} />

          {repair.status !== "completed" && repair.status !== "cancelled" ? (
            <RepairComposer repairId={repair.id} />
          ) : null}

          <section>
            <h3 className="text-sm uppercase tracking-wider text-ink-dim mb-2">Timeline</h3>
            {loading ? (
              <p className="text-sm text-ink-dim">Loading…</p>
            ) : events.length === 0 ? (
              <p className="text-sm text-ink-dim">No events yet.</p>
            ) : (
              <ol className="flex flex-col gap-3">
                {events.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-3">
                    <span className="mt-1 w-2 h-2 rounded-full bg-accent shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{REPAIR_STATUS_LABEL[ev.status]}</p>
                      <p className="text-xs text-ink-dim">{formatDateGB(ev.created_at)}</p>
                      {ev.note ? <p className="text-sm mt-1">{ev.note}</p> : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
